import { useCallback } from 'react'
import {
  getCurrentChatInput,
  useActiveAudioNoteId,
  useCloseActiveAudioNote,
  useIsQueueFull,
  useIsTurnLive,
} from '../../../../store'
import { useIsDictating, useIsVoiceModeActive } from '../../../../store/atoms'
import { useSubmitMessage } from '../../../../hooks'
import { useSubmitChatInput } from '../../../../hooks/useSubmitChatInput'
import { useSkillCompareSubmit } from '../../../../hooks/use-skill-compare'
import { SendMessageOptions } from '../../../../types'

interface UseSubmitComposerOptions {
  chatId?: string
  onSubmit?: (conversationId: string) => void
}

/**
 * Shared submit-gating and dispatch for the composer, consumed by both the
 * Enter keydown handler and the submit button so they never diverge.
 *
 * The two callers block on slightly different queue semantics: the button
 * disables on any full queue, while Enter only blocks a full queue while the
 * chat is actively streaming (an idle-but-full queue still lets Enter
 * submit) — both callers block on dictation/voice mode identically.
 */
export function useSubmitComposer({ chatId, onSubmit }: UseSubmitComposerOptions) {
  const isCurrentChatStreaming = useIsTurnLive(chatId)
  const isQueueFull = useIsQueueFull(chatId)
  const isDictating = useIsDictating()
  const isVoiceModeActive = useIsVoiceModeActive()
  const activeAudioNoteId = useActiveAudioNoteId()
  const closeActiveAudioNote = useCloseActiveAudioNote()
  const handleChatInputSubmitted = useSubmitChatInput()
  const submitMessage = useSubmitMessage()
  const submitSkillCompare = useSkillCompareSubmit()

  const isBlockedByVoice = isDictating || isVoiceModeActive
  const isBlockedForButton = isQueueFull || isBlockedByVoice
  const isBlockedForKeyboard = (isCurrentChatStreaming && isQueueFull) || isBlockedByVoice

  const dispatchSubmit = useCallback(
    (overrides?: Partial<SendMessageOptions>) => {
      const options: SendMessageOptions = {
        conversationId: chatId,
        // Force new chat if no chatId is provided (submitting from home)
        isNewChat: !chatId,
        usePreparedConversation: !chatId,
        onSubmit,
        ...overrides,
      }

      if (activeAudioNoteId) {
        const currentInput = getCurrentChatInput()
        if (currentInput.trim()) {
          submitMessage(currentInput, options)
          closeActiveAudioNote()
        }
      } else {
        // Skill compare (dev only) runs the turn as both sides of a pair; anything else submits normally.
        void submitSkillCompare(options).then((compared) => {
          if (!compared) handleChatInputSubmitted(options)
        })
      }
    },
    [
      chatId,
      onSubmit,
      activeAudioNoteId,
      submitMessage,
      closeActiveAudioNote,
      handleChatInputSubmitted,
      submitSkillCompare,
    ],
  )

  return { isQueueFull, isBlockedForButton, isBlockedForKeyboard, dispatchSubmit }
}
